import { Box, Chip, styled, Typography } from '@mui/material';
import { motion } from 'framer-motion';

import { Troubleshooting } from '../../../components/troubleshooting/troubleshooting';
import ZaraAvatar from '../../../components/zara-avatar/zara-avatar';
import formatDuration from '../../../utils/format-duration';
import { InteractionState } from '../types';

type ControlPanelProps = {
  interactionState: InteractionState;
  avServiceAnalyser?: AnalyserNode | null;
  assessmentId: string;
  timeLeft: number | null;
  onUserEndedInterview: () => void;
  isLoading?: boolean;
};

export const IndicatorContainer = styled(motion.div)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  alignItems: 'center',
  position: 'absolute',
  bottom: '8px',
  width: '100%',
});

export const PillContainer = styled(motion.div)({
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  borderRadius: 16,
  minWidth: '250px',
  display: 'flex',
  padding: '8px',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
  border: '1px solid rgba(255, 255, 255, 0.18)',
  boxShadow: '0 8px 32px 0 rgba(135, 135, 135, 0.37)',
  position: 'relative',
});

const AnimationContainer = styled(Box)({
  position: 'absolute',
  left: '8px',
  top: '50%',
  transform: 'translateY(-50%)',
});

const FlashingText = styled(Typography)<{ isFlashing: boolean }>(
  ({ isFlashing }) => ({
    animation: isFlashing ? 'flash 1s infinite' : 'none',
    '@keyframes flash': {
      '0%, 100%': {
        color: 'white',
      },
      '50%': {
        color: '#f44336',
      },
    },
  }),
);

export const IconWrapper = styled(motion.div)<{
  isActive: boolean;
  color: string;
}>(({ isActive, color }) => ({
  backgroundColor: isActive ? `${color}99` : 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(5px)',
  borderRadius: 16,
  padding: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: isActive ? 1 : 0.5,
  border: '1px solid rgba(255, 255, 255, 0.18)',
}));

const ControlPanel = ({
  interactionState,
  avServiceAnalyser,
  assessmentId,
  timeLeft,
  onUserEndedInterview,
  isLoading = false,
}: ControlPanelProps) => {
  const showTimer = !isLoading && timeLeft !== null;

  return (
    <Box
      width='100%'
      height='calc(100% - 60px)'
      position='absolute'
      zIndex={50}
    >
      <IndicatorContainer>
        <PillContainer
          initial={{ opacity: 1, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <AnimationContainer>
            <Box sx={{ width: '100px', height: '100px', position: 'relative' }}>
              <ZaraAvatar
                isAiSpeaking={interactionState === 'aiSpeaking'}
                avServiceAnalyser={avServiceAnalyser}
                isLoading={isLoading}
                size={100}
                pointSize={0.6}
              />
            </Box>
          </AnimationContainer>

          <Box
            sx={{
              display: 'flex',
              ml: '100px',
              paddingLeft: '10px',
              minWidth: '120px',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {showTimer && timeLeft !== null && (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {timeLeft < 60 ? (
                    <>
                      <Box sx={{ opacity: 0.7 }}>
                        <FlashingText
                          variant='h6'
                          isFlashing={true}
                          sx={{ userSelect: 'none' }}
                        >
                          {formatDuration(timeLeft)}
                        </FlashingText>
                        <Chip
                          label='Interview ending soon'
                          color='error'
                          size='small'
                          sx={{ borderRadius: '6px' }}
                        />
                      </Box>
                    </>
                  ) : (
                    <Typography
                      variant='h5'
                      sx={{ color: 'white', userSelect: 'none' }}
                    >
                      {formatDuration(timeLeft)}
                    </Typography>
                  )}
                </Box>
              )}

              {isLoading ? (
                <Typography variant='body2' sx={{ color: 'white' }}>
                  Zara is joining soon
                </Typography>
              ) : (
                <Typography variant='body2' sx={{ color: 'white' }}>
                  {interactionState === 'aiSpeaking' && 'Zara is speaking'}
                  {interactionState === 'idle' && 'Please speak'}
                  {interactionState === 'recording' && 'Zara is listening'}
                </Typography>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Troubleshooting
                  assessmentId={assessmentId}
                  onUserEndedInterview={onUserEndedInterview}
                />
              </Box>
            </Box>
          </Box>
        </PillContainer>
      </IndicatorContainer>
    </Box>
  );
};

export default ControlPanel;
